// frontend/shared-ui/src/components/Grid.jsx
import React, { useState } from "react";
import Spinner from "./Spinner";
import Checkbox from "./Checkbox";

/* A tiny internal component to handle image loading/error states */
function GridImage({ src, alt }) {
  const [error, setError] = useState(false);
  if (!src || error) {
    return (
      <div className="fh-grid__image">
        <div className="fh-grid__image-placeholder">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
      </div>
    );
  }
  return (
    <div className="fh-grid__image">
      <img src={src} alt={alt} loading="lazy" onError={() => setError(true)} />
    </div>
  );
}

export default function Grid({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = "No items found",
  getId = (row) => row.id,
  onCardClick,
  cardVariant = "default",
  columnsPerRow,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  titleColumn = null,
  imageColumn = null,
  headerExtra,
  footerExtra,
  cardClassName = "",
}) {
  const selectedSet = new Set((selectedIds || []).map(String));
  const visibleColumns = columns.filter((c) => !c.hideInGrid);

  const toggleOne = (id, checked) => {
    if (!onSelectionChange) return;
    const idStr = String(id);
    const next = new Set(selectedIds.map(String));
    if (checked) next.add(idStr);
    else next.delete(idStr);
    const allIds = data.map(getId);
    const map = new Map(allIds.map((x) => [String(x), x]));
    onSelectionChange([...next].map((s) => map.get(s) ?? s));
  };

  const gridStyle = columnsPerRow
    ? { gridTemplateColumns: `repeat(${columnsPerRow}, minmax(0, 1fr))` }
    : undefined;

  if (loading) {
    return (
      <div className="fh-grid-state">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="fh-grid-state fh-grid-state--empty">{emptyMessage}</div>
    );
  }

  return (
    <div className={`fh-grid fh-grid--${cardVariant}`} style={gridStyle}>
      {data.map((row, idx) => {
        const id = getId(row);
        const isSelected = selectedSet.has(String(id));

        // Safely resolve image URL
        let imgUrl = null;
        if (imageColumn) {
          imgUrl =
            typeof imageColumn === "function"
              ? imageColumn(row)
              : row[imageColumn];
        }

        const title = titleColumn
          ? typeof titleColumn === "function"
            ? titleColumn(row)
            : row[titleColumn]
          : null;

        return (
          <div
            key={id ?? idx}
            className={[
              "fh-grid__card",
              isSelected ? "is-selected" : "",
              onCardClick ? "is-clickable" : "",
              cardClassName,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={
              onCardClick
                ? (e) => {
                    if (
                      e.target.closest(".fh-checkbox-control") ||
                      e.target.closest("button") ||
                      e.target.closest("a")
                    )
                      return;
                    onCardClick(row);
                  }
                : undefined
            }
          >
            {/* Selection checkbox overlay */}
            {selectable && (
              <div
                className="fh-grid__select"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={(checked) => toggleOne(id, checked)}
                  ariaLabel={`Select item ${idx + 1}`}
                />
              </div>
            )}

            {/* Header extras (badges) */}
            {headerExtra && (
              <div className="fh-grid__header-extra">{headerExtra(row)}</div>
            )}

            {/* Image — use the internal GridImage component */}
            <GridImage src={imgUrl} alt={title || `Item ${idx + 1}`} />

            {/* Body content */}
            <div className="fh-grid__body">
              {title && <div className="fh-grid__title">{title}</div>}

              <div className="fh-grid__fields">
                {visibleColumns.map((col) => {
                  if (col.key === titleColumn) return null;
                  if (imageColumn && col.key === imageColumn) return null;
                  const value = col.render ? col.render(row) : row[col.key];
                  if (value === undefined || value === null || value === "")
                    return null;
                  return (
                    <div
                      key={col.key}
                      className="fh-grid__field"
                      style={{
                        gridColumn: `span ${Math.min(12, col.span || 6)}`,
                      }}
                    >
                      {col.label && (
                        <span className="fh-grid__field-label">
                          {col.label}
                        </span>
                      )}
                      <span className="fh-grid__field-value">{value}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer actions */}
            {footerExtra && (
              <div className="fh-grid__footer">{footerExtra(row)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
